import React, { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
} from '@mui/material';
import {
    LocalShipping as ShippingIcon,
    People as PeopleIcon,
    AttachMoney as MoneyIcon,
    Add as AddIcon,
    Assignment as AssignmentIcon,
    LocationOn as LocationIcon,
    TrendingUp as TrendingUpIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import franchiseService from '../../services/franchiseService';
import authService from '../../services/authService';

const StatCard = ({ title, value, icon, color, loading = false }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                    backgroundColor: `${color}20`,
                    borderRadius: '50%',
                    p: 1,
                    mr: 2
                }}>
                    {React.cloneElement(icon, { sx: { color: color } })}
                </Box>
                <Typography variant="h6" component="div">
                    {title}
                </Typography>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {loading ? <CircularProgress size={24} /> : value}
            </Typography>
        </CardContent>
    </Card>
);

const QuickAction = ({ icon, primary, secondary, onClick }) => (
    <>
        <ListItem button onClick={onClick}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={primary} secondary={secondary} />
        </ListItem>
        <Divider />
    </>
);

const FranchiseDashboard = () => {
    const navigate = useNavigate();
    const { showError } = useApiHandler();
    const [stats, setStats] = useState({
        total_bookings: 0,
        this_month_bookings: 0,
        total_revenue: 0,
        this_month_revenue: 0,
        average_booking_value: 0
    });
    const [loading, setLoading] = useState(true);
    const [recentBookings, setRecentBookings] = useState([]);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const user = authService.getCurrentUser();
            
            if (user && user.id) {
                // Load franchise statistics
                const statsResponse = await franchiseService.getFranchiseStatistics(user.id);
                if (statsResponse.success) {
                    setStats(statsResponse.data);
                }

                // Load recent bookings (mock data for now)
                setRecentBookings([
                    {
                        id: 1,
                        awbNo: 'SX24001234',
                        customerName: 'John Doe',
                        destination: 'Mumbai',
                        status: 'In Transit',
                        amount: 450,
                        date: '2024-03-15',
                    },
                    {
                        id: 2,
                        awbNo: 'SX24001235',
                        customerName: 'Jane Smith',
                        destination: 'Delhi',
                        status: 'Delivered',
                        amount: 320,
                        date: '2024-03-14',
                    },
                    {
                        id: 3,
                        awbNo: 'SX24001236',
                        customerName: 'Bob Johnson',
                        destination: 'Bangalore',
                        status: 'Picked Up',
                        amount: 280,
                        date: '2024-03-13',
                    },
                ]);
            }
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
            showError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'delivered':
                return 'success';
            case 'in transit':
                return 'warning';
            case 'picked up':
                return 'info';
            default:
                return 'default';
        }
    };

    const handleCreateBooking = () => {
        navigate('/franchise/bookings/create');
    };

    const handleViewBookings = () => {
        navigate('/franchise/bookings');
    };

    const handleViewReports = () => {
        navigate('/franchise/reports');
    };

    const handleViewBooking = (bookingId) => {
        navigate(`/franchise/bookings/${bookingId}`);
    };

    return (
        <DashboardLayout title="Franchise Dashboard" userType="franchise">
            <Grid container spacing={3}>
                {/* Statistics Cards */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Bookings"
                        value={stats.total_bookings || 0}
                        icon={<ShippingIcon />}
                        color="#1976d2"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="This Month"
                        value={stats.this_month_bookings || 0}
                        icon={<TrendingUpIcon />}
                        color="#2e7d32"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Revenue"
                        value={`₹${(stats.total_revenue || 0).toLocaleString()}`}
                        icon={<MoneyIcon />}
                        color="#ed6c02"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Monthly Revenue"
                        value={`₹${(stats.this_month_revenue || 0).toLocaleString()}`}
                        icon={<MoneyIcon />}
                        color="#d32f2f"
                        loading={loading}
                    />
                </Grid>

                {/* Quick Actions */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Quick Actions
                        </Typography>
                        <List>
                            <QuickAction
                                icon={<AddIcon />}
                                primary="New Booking"
                                secondary="Create a new shipment booking"
                                onClick={handleCreateBooking}
                            />
                            <QuickAction
                                icon={<ShippingIcon />}
                                primary="Manage Bookings"
                                secondary="View and update booking status"
                                onClick={handleViewBookings}
                            />
                            <QuickAction
                                icon={<AssignmentIcon />}
                                primary="Reports"
                                secondary="View performance reports"
                                onClick={handleViewReports}
                            />
                        </List>
                    </Paper>
                </Grid>

                {/* Performance Summary */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Performance Summary
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <TrendingUpIcon color="success" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={`Average Booking Value: ₹${Math.round(stats.average_booking_value || 0)}`}
                                    secondary="Based on all bookings"
                                />
                            </ListItem>
                            <Divider />
                            <ListItem>
                                <ListItemIcon>
                                    <MoneyIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={`Monthly Target: ₹${(stats.this_month_revenue || 0).toLocaleString()}`}
                                    secondary="Current month progress"
                                />
                            </ListItem>
                            <Divider />
                            <ListItem>
                                <ListItemIcon>
                                    <ShippingIcon color="info" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={`Growth Rate: ${stats.this_month_bookings > 0 ? '+' : ''}${stats.this_month_bookings}`}
                                    secondary="Bookings this month"
                                />
                            </ListItem>
                        </List>
                    </Paper>
                </Grid>

                {/* Recent Bookings */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">Recent Bookings</Typography>
                            <Button 
                                variant="outlined" 
                                size="small"
                                onClick={handleViewBookings}
                            >
                                View All
                            </Button>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>AWB No</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Destination</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <CircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    ) : recentBookings.length > 0 ? (
                                        recentBookings.map((booking) => (
                                            <TableRow key={booking.id}>
                                                <TableCell>{booking.awbNo}</TableCell>
                                                <TableCell>{booking.customerName}</TableCell>
                                                <TableCell>{booking.destination}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={booking.status}
                                                        color={getStatusColor(booking.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>₹{booking.amount}</TableCell>
                                                <TableCell>{booking.date}</TableCell>
                                                <TableCell>
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={() => handleViewBooking(booking.id)}
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <Typography variant="body2" color="textSecondary">
                                                    No recent bookings found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </DashboardLayout>
    );
};

export default FranchiseDashboard; 